import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import clsx from 'clsx';

const TITULO = { ingreso: 'Ingresos', costo: 'Costos', gasto: 'Gastos' };

/**
 * Elige una cuenta de movimiento del catálogo de la empresa, buscando por código o nombre.
 * Las agrupadoras no se ofrecen: no tienen asientos propios y su real siempre daría cero.
 *
 * props: cuentas (de /presupuesto/catalogo-contable), value (id), onChange(id | null),
 *        tipos (['ingreso','costo','gasto'] por omisión), placeholder, compacto, disabled
 */
export default function SelectorCuenta({ cuentas = [], value, onChange, tipos = ['ingreso', 'costo', 'gasto'], placeholder = 'Elegir cuenta…', compacto = false, disabled = false }) {
  const [abierto, setAbierto] = useState(false);
  const [busca, setBusca] = useState('');
  const ref = useRef(null);
  const elegida = cuentas.find(c => c.id === Number(value));
  const opciones = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return cuentas.filter(c => c.movimiento && tipos.includes(c.tipo) && (!q || c.codigo.toLowerCase().includes(q) || c.nombre.toLowerCase().includes(q)));
  }, [cuentas, tipos, busca]);

  useEffect(() => {
    if (!abierto) return undefined;
    const cerrar = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', cerrar);
    return () => document.removeEventListener('mousedown', cerrar);
  }, [abierto]);

  return (
    <div ref={ref} className="relative">
      <button type="button" disabled={disabled} onClick={() => { setAbierto(!abierto); setBusca(''); }}
        className={clsx('w-full flex items-center gap-2 border border-gray-300 rounded-lg bg-white text-left disabled:bg-gray-50 disabled:text-gray-400', compacto ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm')}>
        <span className={clsx('truncate flex-1', !elegida && 'text-gray-400')}>{elegida ? <><span className="font-mono text-gray-500">{elegida.codigo}</span> {elegida.nombre}</> : placeholder}</span>
        {elegida && !disabled && <X size={14} className="text-gray-400 hover:text-gray-600 shrink-0" onClick={(e) => { e.stopPropagation(); onChange(null); }} />}
        <ChevronDown size={14} className="text-gray-400 shrink-0" />
      </button>
      {abierto && (
        <div className="absolute z-50 mt-1 w-full min-w-[320px] bg-white border border-gray-200 rounded-lg shadow-xl">
          <input autoFocus value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por código o nombre" className="w-full px-3 py-2 text-sm border-b border-gray-100 outline-none rounded-t-lg" />
          <div className="max-h-72 overflow-y-auto py-1">
            {tipos.map(t => {
              const deTipo = opciones.filter(c => c.tipo === t);
              if (!deTipo.length) return null;
              return (
                <div key={t}>
                  <div className="px-3 pt-2 pb-1 text-[10.5px] font-semibold tracking-wider uppercase text-gray-400">{TITULO[t]}</div>
                  {deTipo.map(c => (
                    <button type="button" key={c.id} onClick={() => { onChange(c.id); setAbierto(false); }}
                      className={clsx('w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50', c.id === Number(value) && 'bg-indigo-50 font-medium')}>
                      <span className="font-mono text-gray-500 mr-1.5">{c.codigo}</span>{c.nombre}
                    </button>
                  ))}
                </div>
              );
            })}
            {!opciones.length && <div className="px-3 py-4 text-sm text-gray-400 text-center">Sin cuentas que coincidan</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/** Centro de costo: solo los que admiten movimiento. Sin catálogo no se muestra nada. */
export function SelectorCentro({ centros = [], value, onChange, compacto = false, disabled = false }) {
  const opciones = centros.filter(c => c.movimiento);
  if (!opciones.length) return null;
  return (
    <select value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      className={clsx('w-full border border-gray-300 rounded-lg bg-white', compacto ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm')}>
      <option value="">Sin centro de costo</option>
      {opciones.map(c => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
    </select>
  );
}
